import React, { useCallback, useEffect, useState } from 'react';
import { adminApi, rosterApi } from '../services/api';
import Brand from './Brand';
import HeaderNav from './HeaderNav';
import UserActivityPanel from './UserActivityPanel';
import { planLabel as getPlanLabel } from '../utils/planLabels';

const PLAN_DETAILS = {
  tier_0: ['1 admin-assigned First Look'],
  client: ['5 chosen First Looks', 'All admin-published Client First Looks'],
  employee: ['All First Look reports'],
  tier_1: ['7 First Looks/month', '1 Deep Dive'],
  tier_2: ['14 First Looks/month', '7 Deep Dives'],
  tier_3: ['35 First Looks/month', 'Unlimited Deep Dives'],
  tier_4: ['Custom customer plan']
};

const PLAN_OPTIONS = [['tier_0', 'Free Tier'], ['client', 'Client'], ['employee', 'Employee'], ['tier_1', 'Tier 1'], ['tier_2', 'Tier 2'], ['tier_3', 'Tier 3'], ['tier_4', 'Tier 4 (Custom)']];
const DEEPDIVE_TAB_OPTIONS = [{ id: 'starthere', label: 'Start Here' }, { id: 'angle', label: 'Hear Story' }, { id: 'summary', label: 'Check Numbers' }, { id: 'business', label: 'Size Moat' }, { id: 'capital', label: 'Follow Money' }, { id: 'valuation', label: 'Hear Bear Case' }, { id: 'competition', label: 'Meet Rivals' }, { id: 'news', label: 'Read News' }, { id: 'glossary', label: 'Decode Jargon' }, { id: 'gapanswers', label: 'Answer Questions' }, { id: 'filingdelta', label: 'Read Fine Print' }, { id: 'insider', label: 'Watch Insiders' }, { id: 'changes', label: 'See Changes' }];
const TIER_1_TABS = ['starthere', 'angle', 'business', 'valuation', 'competition', 'glossary'];
const TIER_2_TABS = [...TIER_1_TABS, 'summary', 'capital', 'news'];
const TIER_3_TABS = [...TIER_2_TABS, 'gapanswers', 'filingdelta', 'insider', 'changes'];
const DEFAULT_PLAN_DATA = {
  catalog: {
    version: 0,
    plans: {
      tier_0: { label: 'Free Tier', rank: 0, companies_per_period: 0, firstlook_picks_per_period: 0, full_reports: 0, deepdive_tabs: [], firstlook_ticker: 'TSLA', all_firstlook: false, credits_per_period: 0, included_tickers: [], customer_specific: false, price_amount: null, price_currency: 'USD', billing_period: 'month' },
      client: { label: 'Client', rank: 1, companies_per_period: 5, firstlook_picks_per_period: 5, full_reports: 0, deepdive_tabs: [], firstlook_ticker: '', all_firstlook: false, credits_per_period: 0, included_tickers: [], customer_specific: false, price_amount: null, price_currency: 'USD', billing_period: 'month' },
      employee: { label: 'Employee', rank: 2, companies_per_period: null, firstlook_picks_per_period: null, full_reports: null, deepdive_tabs: null, firstlook_ticker: '', all_firstlook: true, credits_per_period: 0, included_tickers: [], customer_specific: false, price_amount: null, price_currency: 'USD', billing_period: 'month' },
      tier_1: { label: 'Tier 1', rank: 3, companies_per_period: 7, firstlook_picks_per_period: 7, full_reports: 1, deepdive_tabs: TIER_1_TABS, firstlook_ticker: '', all_firstlook: false, credits_per_period: 0, included_tickers: [], customer_specific: false, price_amount: null, price_currency: 'USD', billing_period: 'month' },
      tier_2: { label: 'Tier 2', rank: 4, companies_per_period: 14, firstlook_picks_per_period: 14, full_reports: 7, deepdive_tabs: TIER_2_TABS, firstlook_ticker: '', all_firstlook: false, credits_per_period: 0, included_tickers: [], customer_specific: false, price_amount: null, price_currency: 'USD', billing_period: 'month' },
      tier_3: { label: 'Tier 3', rank: 5, companies_per_period: 35, firstlook_picks_per_period: 35, full_reports: null, deepdive_tabs: TIER_3_TABS, firstlook_ticker: '', all_firstlook: false, credits_per_period: 0, included_tickers: [], customer_specific: false, price_amount: null, price_currency: 'USD', billing_period: 'month' },
      tier_4: { label: 'Tier 4 (Custom)', rank: 6, companies_per_period: 0, firstlook_picks_per_period: 0, full_reports: 0, deepdive_tabs: null, firstlook_ticker: '', all_firstlook: false, credits_per_period: 0, included_tickers: [], customer_specific: true, price_amount: null, price_currency: 'USD', billing_period: 'month' }
    }
  },
  fields: [
    { name: 'label', kind: 'text', default: '' },
    { name: 'rank', kind: 'int', default: 0 },
    { name: 'companies_per_period', kind: 'count?', default: 0 },
    { name: 'firstlook_picks_per_period', kind: 'count?', default: 0 },
    { name: 'pick_days', kind: 'count?', default: null },
    { name: 'full_reports', kind: 'count?', default: 0 },
    { name: 'deepdive_tabs', kind: 'tabs?', default: null },
    { name: 'complimentary_reports', kind: 'bool', default: false },
    { name: 'included_tickers', kind: 'tickers', default: [] },
    { name: 'credits_per_period', kind: 'int', default: 0 },
    { name: 'onboard_requests_per_period', kind: 'int', default: 0 },
    { name: 'coverage_sla_hours', kind: 'count?', default: null },
    { name: 'customer_specific', kind: 'bool', default: false },
    { name: 'price_amount', kind: 'money?', default: null },
    { name: 'price_currency', kind: 'currency', default: 'USD' },
    { name: 'billing_period', kind: 'billing_period', default: 'month' },
    { name: 'firstlook_ticker', kind: 'ticker?', default: '' },
    { name: 'all_firstlook', kind: 'bool', default: false }
  ],
  billing_periods: ['month', 'quarter', 'year', 'one_time', 'custom'],
  deepdive_tabs: DEEPDIVE_TAB_OPTIONS
};
const CONTEXT_SIZE_OPTIONS = [['', 'Provider default'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High']];
const REASONING_EFFORT_OPTIONS = [['', 'Provider default'], ['none', 'None'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High'], ['xhigh', 'Extra high']];
const MODEL_NAMES = {
  'claude-haiku-5-5': 'Claude Haiku 5.5',
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-sonnet-5-5': 'Claude Sonnet 5.5'
};
const modelOptionLabel = (option) => {
  const name = Object.keys(MODEL_NAMES).find((id) => option.model_id.endsWith(id));
  return name ? `${MODEL_NAMES[name]} (${option.provider})` : option.model_id;
};
const PLAN_ID_RE = /^[a-z][a-z0-9_]{1,31}$/;
const RESERVED_PLAN_IDS = new Set(['public', 'showcase', 'full_report', 'unlock', 'admin', 'free', 'pro', 'enterprise']);
const COVERAGE_STATUSES = ['', 'pending', 'building', 'delivered', 'refused', 'failed', 'cancelled'];
const EMPTY_COMPANY_FILTERS = { status: '', tracked: '', subsection: '', live: '', error: '', pharma: '' };
const RESEARCH_FAILURE_STATUSES = new Set(['failed', 'conflict', 'too_large']);
const PROFILE_LEVELS = ['no_profile', 'lite', 'full'];
const companyDateValue = (value) => {
  const timestamp = value ? Date.parse(value) : NaN;
  return Number.isNaN(timestamp) ? null : timestamp;
};
const COMPANY_COLUMNS = [
  { key: 'company', label: 'Company', value: (company) => company.ticker },
  { key: 'subsection', label: 'Subsection', value: (company) => company.subsection || null },
  { key: 'profile', label: 'Profile', value: (company) => company.rosterCompany?.profile_level || null },
  { key: 'live', label: 'Status', value: (company) => company.liveStatus },
  { key: 'pharma', label: 'Pharma Landscape', value: (company) => company.rosterCompany ? Number(Boolean(company.rosterCompany.pharma_landscape)) : null },
  { key: 'tracked', label: 'Tracked by', value: (company) => company.subscriptions.length },
  { key: 'updated', label: 'Last updated', value: (company) => companyDateValue(company.research?.last_updated) },
  { key: 'status', label: 'Latest update', value: (company) => company.latestStatus.replaceAll('_', ' ') },
  { key: 'failure', label: 'Last failure', value: (company) => companyDateValue(company.research?.last_failure_at) },
  { key: 'actions', label: 'Actions', value: (company) => company.rosterCompany ? `${company.rosterCompany.live ? 'Pause' : 'Resume'} Build` : 'Build' }
];

const parseInviteCsv = (text) => {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"' && quoted && text[index + 1] === '"') {
      cell += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === ',' && !quoted) {
      row.push(cell.trim());
      cell = '';
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      row.push(cell.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += character;
    }
  }
  row.push(cell.trim());
  if (row.some(Boolean)) rows.push(row);

  if (rows.length && rows[0][0]?.charCodeAt(0) === 0xFEFF) {
    rows[0][0] = rows[0][0].slice(1);
  }
  const emailColumn = rows[0]?.findIndex((value) => /^e-?mail(?: address)?$/i.test(value)) ?? -1;
  const dataRows = emailColumn >= 0 ? rows.slice(1) : rows;
  const column = emailColumn >= 0 ? emailColumn : 0;
  const emails = [];
  const invalid = [];
  const seen = new Set();
  let duplicates = 0;

  dataRows.forEach((values, index) => {
    const email = (values[column] || '').trim().toLowerCase();
    if (!email) {
      invalid.push(index + 1);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      invalid.push(index + 1);
    } else if (seen.has(email)) {
      duplicates += 1;
    } else {
      seen.add(email);
      emails.push(email);
    }
  });

  return { emails, invalid, duplicates };
};

const formatDate = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
};

const formatUsd = (value) => (typeof value === 'number' ? `$${value.toFixed(2)}` : '—');
const formatTokens = (value) => (typeof value === 'number' ? value.toLocaleString() : '—');
const formatMinutes = (seconds) => (typeof seconds === 'number' ? `${(seconds / 60).toFixed(1)}m` : '—');
const parseTickers = (text) => text.split(',').map((ticker) => ticker.trim().toUpperCase()).filter(Boolean);
const plural = (count, noun) => `${count == null ? 'unlimited' : count} ${noun}${count === 1 ? '' : 's'}`;
const PLAN_FIELD_LABELS = {
  companies_per_period: 'Tracked company cap',
  firstlook_picks_per_period: 'First Look reports / month',
  full_reports: 'Deep Dive selections',
  deepdive_tabs: 'Deep Dive tabs',
  onboard_requests_per_period: 'Coverage requests / month'
};
const accessAudienceLabel = (audience, labels) => ({
  full_report: 'Deep Dive selection',
  unlock: 'Temporary access (legacy)'
}[audience] || labels?.[audience] || audience.replaceAll('_', ' '));
const planSummary = (plan) => `${plan.all_firstlook ? 'All First Looks' : `${plan.firstlook_picks_per_period ?? 'Unlimited'} First Looks/month`} · ${plural(plan.full_reports, 'Deep Dive')}`;
const planSummaryParts = (plan) => [
  plan.all_firstlook ? 'all First Look reports' : `${plan.firstlook_picks_per_period ?? 'unlimited'} First Look reports/month`,
  plan.complimentary_reports ? 'all shared Client First Looks' : '',
  plan.firstlook_ticker ? `assigned First Look: ${plan.firstlook_ticker}` : '',
  plural(plan.full_reports, 'Deep Dive')
].filter(Boolean);

const AdminPage = ({ user, employeeAccess = false, onLogout, onBack, onOpenProfile }) => {
  const [tab, setTab] = useState(employeeAccess ? 'companies' : 'users');
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trackedCompanies, setTrackedCompanies] = useState([]);
  const [canDeleteCompany, setCanDeleteCompany] = useState(false);
  const [clientReports, setClientReports] = useState([]);
  const [tamarac, setTamarac] = useState(null);
  const [costs, setCosts] = useState({ companies: [], runs: [], by_stage: [], by_model: [], total_cost_usd: 0 });
  const [runtimeConfig, setRuntimeConfig] = useState(null);
  const [runtimeConfigLoading, setRuntimeConfigLoading] = useState(false);
  const [visibilityConfig, setVisibilityConfig] = useState(null);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [coverageRequests, setCoverageRequests] = useState([]);
  const [coverageStatus, setCoverageStatus] = useState('');
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [plansConfig, setPlansConfig] = useState({ catalog: { version: 0, plans: {} }, fields: [] });
  const [plansLoading, setPlansLoading] = useState(false);
  const [newPlanId, setNewPlanId] = useState('');
  const [tickerText, setTickerText] = useState({});
  const [showcaseText, setShowcaseText] = useState('');
  const [hubBuildSelection, setHubBuildSelection] = useState({});
  const [hubBuildTaxonomy, setHubBuildTaxonomy] = useState(true);
  const [reportTicker, setReportTicker] = useState('');
  const [reportName, setReportName] = useState('');
  const [trackedTicker, setTrackedTicker] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [query, setQuery] = useState('');
  const [userView, setUserView] = useState('accounts');
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePlan, setInvitePlan] = useState('client');
  const [inviteIsClient, setInviteIsClient] = useState(false);
  const [bulkEmails, setBulkEmails] = useState([]);
  const [bulkInvalidRows, setBulkInvalidRows] = useState([]);
  const [bulkDuplicateCount, setBulkDuplicateCount] = useState(0);
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkProgress, setBulkProgress] = useState('');
  const [expiryDates, setExpiryDates] = useState({});
  const [expandedAccount, setExpandedAccount] = useState('');
  const [expandedCompany, setExpandedCompany] = useState('');
  const [companyFilters, setCompanyFilters] = useState(EMPTY_COMPANY_FILTERS);
  const [companySort, setCompanySort] = useState({ key: 'subsection', direction: 'ascending' });

  const loadData = useCallback(async () => {
    if (employeeAccess) {
      const [companyResult, reportResult, tamaracResult, rosterResult] = await Promise.all([
        adminApi.companies(),
        adminApi.clientReports(),
        adminApi.tamaracSync ? adminApi.tamaracSync().catch(() => null) : Promise.resolve(null),
        rosterApi.adminCompanies()
      ]);
      setCompanies(companyResult.companies || []);
      setTrackedCompanies(rosterResult.items || []);
      setCanDeleteCompany(rosterResult.can_delete === true);
      setClientReports(reportResult.reports || []);
      setTamarac(tamaracResult);
      return;
    }
    const [userResult, companyResult, reportResult, costResult, planResult, tamaracResult, rosterResult] = await Promise.all([
      adminApi.users(),
      adminApi.companies(),
      adminApi.clientReports(),
      adminApi.costs(),
      adminApi.plans ? adminApi.plans().catch(() => DEFAULT_PLAN_DATA) : Promise.resolve(DEFAULT_PLAN_DATA),
      adminApi.tamaracSync ? adminApi.tamaracSync().catch(() => null) : Promise.resolve(null),
      rosterApi.adminCompanies()
    ]);
    setUsers(userResult.users || []);
    setCompanies(companyResult.companies || []);
    setTrackedCompanies(rosterResult.items || []);
    setCanDeleteCompany(rosterResult.can_delete === true);
    setClientReports(reportResult.reports || []);
    setTamarac(tamaracResult);
    setCosts(costResult);
    setPlansConfig(planResult);
    setTickerText(Object.fromEntries(Object.entries(planResult.catalog?.plans || {}).map(([planId, plan]) => [`${planId}:included_tickers`, (plan.included_tickers || []).join(', ')])));
    const firstPlan = Object.keys(planResult.catalog?.plans || {})[0];
    if (firstPlan) setInvitePlan((current) => planResult.catalog.plans[current] ? current : firstPlan);
  }, [employeeAccess]);

  const loadRuntimeConfig = async () => {
    setRuntimeConfigLoading(true);
    setError('');
    try {
      setRuntimeConfig(await adminApi.researchConfig());
    } catch (err) {
      setError(err.message);
    } finally {
      setRuntimeConfigLoading(false);
    }
  };

  const loadVisibility = async () => {
    setVisibilityLoading(true);
    setError('');
    try {
      const loaded = await adminApi.visibility();
      setVisibilityConfig(loaded);
      setShowcaseText((loaded.matrix.showcase_tickers || []).join(', '));
    } catch (err) {
      setError(err.message);
    } finally {
      setVisibilityLoading(false);
    }
  };

  const loadCoverage = async (status = coverageStatus) => {
    setCoverageLoading(true);
    setError('');
    try {
      const result = await adminApi.coverage(status || undefined);
      setCoverageRequests(result.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setCoverageLoading(false);
    }
  };

  const loadPlans = async () => {
    setPlansLoading(true);
    setError('');
    try {
      const loaded = await adminApi.plans();
      setPlansConfig(loaded);
      setTickerText(Object.fromEntries(Object.entries(loaded.catalog?.plans || {}).map(([planId, plan]) => [`${planId}:included_tickers`, (plan.included_tickers || []).join(', ')])));
    } catch (err) {
      setError(err.message);
    } finally {
      setPlansLoading(false);
    }
  };

  useEffect(() => {
    loadData()
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [loadData]);

  const perform = async (key, action, successMessage) => {
    setBusyKey(key);
    setError('');
    setNotice('');
    try {
      await action();
      await loadData();
      setNotice(successMessage);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusyKey('');
    }
  };

  const inviteUser = async (event) => {
    event.preventDefault();
    const sent = await perform(
      'invite',
      () => adminApi.invite(inviteEmail.trim(), invitePlan, inviteIsClient || invitePlan === 'client'),
      `Welcome email sent to ${inviteEmail.trim()}. They'll choose a new password when they first sign in.`
    );
    if (sent) setInviteEmail('');
  };

  const uploadInviteList = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setNotice('');
    setBulkFileName(file.name);
    try {
      const parsed = parseInviteCsv(await file.text());
      setBulkEmails(parsed.emails);
      setBulkInvalidRows(parsed.invalid);
      setBulkDuplicateCount(parsed.duplicates);
      if (!parsed.emails.length) {
        setError('No valid email addresses were found. Upload a CSV with an email column or email addresses in the first column.');
      }
    } catch (err) {
      setBulkEmails([]);
      setBulkInvalidRows([]);
      setBulkDuplicateCount(0);
      setError(`Could not read ${file.name}: ${err.message}`);
    }
  };

  const sendBulkInvites = async () => {
    if (!bulkEmails.length || bulkInvalidRows.length || busyKey) return;

    const total = bulkEmails.length;
    const failed = [];
    let sent = 0;
    setBusyKey('bulk-invite');
    setError('');
    setNotice('');
    try {
      for (let index = 0; index < total; index += 1) {
        const email = bulkEmails[index];
        setBulkProgress(`${index + 1} of ${total}`);
        try {
          await adminApi.invite(email, invitePlan, inviteIsClient || invitePlan === 'client');
          sent += 1;
        } catch (err) {
          failed.push({ email, reason: err.message });
        }
      }

      setBulkEmails(failed.map(({ email }) => email));
      setBulkFileName(failed.length ? 'Unsent addresses' : '');
      setBulkDuplicateCount(0);
      if (sent) {
        setNotice(`Invitation emails sent to ${sent} of ${total} clients.`);
        try {
          await loadData();
        } catch (err) {
          setError(`Invitations were sent, but the account list could not be refreshed: ${err.message}`);
        }
      }
      if (failed.length) {
        setError(`Could not invite ${failed.length} client${failed.length === 1 ? '' : 's'}: ${failed.map(({ email, reason }) => `${email} (${reason})`).join('; ')}`);
      }
    } finally {
      setBulkProgress('');
      setBusyKey('');
    }
  };

  const changePlan = (account, plan) => perform(
    `plan-${account.username}`,
    () => adminApi.setPlan(account.username, plan),
    `Plan updated for ${account.email}.`
  );

  const publishReport = async (event) => {
    event.preventDefault();
    const added = await perform('client-report', () => adminApi.addClientReport(reportTicker.trim().toUpperCase(), reportName.trim()), 'Client First Look published.');
    if (added) { setReportTicker(''); setReportName(''); }
  };

  const addTrackedCompany = async (event) => {
    event.preventDefault();
    const ticker = trackedTicker.trim().toUpperCase();
    if (!ticker) return;
    const added = await perform(
      `build-${ticker}`,
      () => rosterApi.buildCompany(ticker),
      `${ticker} research build started. No other primed companies were built.`
    );
    if (added) setTrackedTicker('');
  };

  const setCompanyLive = (company, live) => {
    perform(
      `status-${company.ticker}`,
      () => rosterApi.setCompanyLive(company.ticker, live),
      `${company.ticker} is ${live ? 'live for' : 'paused for'} new picks. Existing user access and reports are unchanged.`
    );
  };

  const setCompanyPharmaLandscape = (company, enabled) => perform(
    `pharma-${company.ticker}`,
    () => rosterApi.setCompanyPharmaLandscape(company.ticker, enabled),
    `${company.ticker} ${enabled ? 'included in' : 'removed from'} Pharma Landscape. The next scheduled refresh will update the shared landscape.`
  );

  const setCompanyProfile = (company, profile) => {
    if (profile === company.profile_level) return;
    const upgrading = PROFILE_LEVELS.indexOf(profile) > PROFILE_LEVELS.indexOf(company.profile_level);
    if (upgrading && !window.confirm(`Upgrade ${company.ticker} to ${profile} and start a research build? This may incur provider and model costs.`)) return;
    perform(
      `profile-${company.ticker}`,
      () => rosterApi.setCompanyProfile(company.ticker, profile),
      `${company.ticker} profile set to ${profile}.${upgrading ? ' Research build queued.' : ' Existing reports are preserved.'}`
    );
  };

  const deleteCompany = (company) => {
    if (!window.confirm(`Remove ${company.ticker} from the QA roster and future roster refreshes? Existing reports, research data and user allocations will be kept. Already-running jobs are not cancelled.`)) return;
    perform(
      `delete-roster-${company.ticker}`,
      () => rosterApi.deleteCompany(company.ticker),
      `${company.ticker} removed from the roster. Existing reports and user allocations were preserved.`
    );
  };

  const startTamaracSync = () => perform(
    'tamarac-sync',
    () => adminApi.startTamaracSync(),
    'Tamarac sync started. Reports can take several minutes; reload to see the result.'
  );

  const updateTuning = (name, value) => {
    setRuntimeConfig((current) => ({
      ...current,
      tuning: { ...current.tuning, [name]: value }
    }));
  };

  const updateModel = (role, name, value) => {
    setRuntimeConfig((current) => {
      const model = { ...current.models[role], [name]: value };
      if (name === 'model_id') {
        const option = current.model_options.find((item) => item.model_id === value);
        if (option) {
          model.provider = option.provider;
          model.cost_in = option.cost_in;
          model.cost_out = option.cost_out;
          model.cost_per_call = option.cost_per_call;
          model.max_tokens = Math.min(model.max_tokens, option.max_tokens);
        }
      } else if (name === 'max_tokens') {
        const option = current.model_options.find((item) => (
          item.model_id === model.model_id && item.provider === model.provider
        ));
        if (option) model.max_tokens = Math.min(value, option.max_tokens);
      }
      return { ...current, models: { ...current.models, [role]: model } };
    });
  };

  const saveRuntimeConfig = async () => {
    setBusyKey('research-config');
    setError('');
    setNotice('');
    try {
      const saved = await adminApi.saveResearchConfig({
        tuning: runtimeConfig.tuning,
        models: runtimeConfig.models
      });
      setRuntimeConfig(saved);
      setNotice('Research defaults saved. New runs will use the updated settings.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const applyCurrentModelPricing = (role) => {
    setRuntimeConfig((current) => {
      const model = current.models[role];
      const option = current.model_options.find((item) => item.model_id === model.model_id && item.provider === model.provider);
      return {
        ...current,
        models: {
          ...current.models,
          [role]: { ...model, cost_in: option.cost_in, cost_out: option.cost_out, cost_per_call: option.cost_per_call }
        }
      };
    });
  };

  const updateVisibilityMatrix = (updater) => {
    setVisibilityConfig((current) => ({ ...current, matrix: updater(current.matrix) }));
  };

  const setVisibilityDefault = (audience, level) => {
    updateVisibilityMatrix((matrix) => {
      const nextItems = Object.fromEntries(Object.entries(matrix.items || {}).map(([itemId, overrides]) => {
        const nextOverrides = { ...overrides };
        if (nextOverrides[audience] === level) delete nextOverrides[audience];
        return [itemId, nextOverrides];
      }).filter(([, overrides]) => Object.keys(overrides).length));
      return { ...matrix, defaults: { ...matrix.defaults, [audience]: level }, items: nextItems };
    });
  };

  const setVisibilityItem = (itemId, audience, level) => {
    updateVisibilityMatrix((matrix) => {
      const defaults = matrix.defaults || {};
      const item = { ...((matrix.items || {})[itemId] || {}) };
      if (level === defaults[audience]) delete item[audience];
      else item[audience] = level;
      const items = { ...(matrix.items || {}) };
      if (Object.keys(item).length) items[itemId] = item;
      else delete items[itemId];
      return { ...matrix, items };
    });
  };

  const setVisibilityAudienceValue = (field, audience, value) => {
    updateVisibilityMatrix((matrix) => ({ ...matrix, [field]: { ...matrix[field], [audience]: value } }));
  };

  const setHubVisibility = (hubId, audience, scope) => {
    updateVisibilityMatrix((matrix) => ({
      ...matrix,
      hubs: {
        ...(matrix.hubs || {}),
        [hubId]: {
          ...((matrix.hubs || {})[hubId] || {}),
          [audience]: scope
        }
      }
    }));
  };

  const toggleHubBuild = (hubId, checked) => {
    setHubBuildSelection((current) => ({ ...current, [hubId]: checked }));
  };

  const rebuildHubs = async () => {
    const selected = Object.entries(hubBuildSelection).filter(([, checked]) => checked).map(([hubId]) => hubId);
    if (!selected.length) {
      setError('Choose at least one hub to rebuild.');
      return;
    }
    setBusyKey('hubs-build');
    setError('');
    setNotice('');
    try {
      const result = await rosterApi.buildHubs(selected, hubBuildTaxonomy);
      setNotice(`Hub rebuild started${result.execution_arn ? `: ${result.execution_arn}` : '.'}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const parseShowcase = (text) => text.split(',').map((ticker) => ticker.trim().toUpperCase()).filter(Boolean);

  const saveVisibility = async () => {
    setBusyKey('visibility');
    setError('');
    setNotice('');
    try {
      const saved = await adminApi.saveVisibility({ ...visibilityConfig.matrix, showcase_tickers: parseShowcase(showcaseText) });
      setVisibilityConfig((current) => ({ ...current, matrix: saved.matrix }));
      setShowcaseText((saved.matrix.showcase_tickers || []).join(', '));
      setNotice('Visibility matrix saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const updatePlanField = (planId, field, value) => {
    setPlansConfig((current) => ({
      ...current,
      catalog: {
        ...current.catalog,
        plans: {
          ...current.catalog.plans,
          [planId]: { ...current.catalog.plans[planId], [field]: value }
        }
      }
    }));
  };

  const addPlan = () => {
    const id = newPlanId.trim();
    if (!PLAN_ID_RE.test(id) || RESERVED_PLAN_IDS.has(id)) {
      setError('Plan id must be lowercase letters, digits, or underscores and not reserved.');
      return;
    }
    if (plansConfig.catalog.plans[id]) {
      setError('That plan id already exists.');
      return;
    }
    const template = Object.fromEntries((plansConfig.fields || []).map((field) => {
      if (field.name === 'customer_specific') return [field.name, id.startsWith('customer_')];
      if (field.name === 'label') return [field.name, id];
      if (Object.prototype.hasOwnProperty.call(field, 'default')) {
        return [field.name, Array.isArray(field.default) ? [...field.default] : field.default];
      }
      if (field.kind === 'text') return [field.name, ''];
      if (field.kind === 'bool') return [field.name, false];
      if (field.kind === 'tickers') return [field.name, []];
      if (field.kind === 'money?') return [field.name, null];
      if (field.kind === 'currency') return [field.name, 'USD'];
      if (field.kind === 'billing_period') return [field.name, field.default || plansConfig.billing_periods?.[0] || 'month'];
      return [field.name, field.kind === 'count?' ? (['companies_per_period', 'full_reports'].includes(field.name) ? 0 : null) : 0];
    }));
    setPlansConfig((current) => ({ ...current, catalog: { ...current.catalog, plans: { ...current.catalog.plans, [id]: template } } }));
    setNewPlanId('');
    setError('');
  };

  const removePlan = (planId) => {
    setPlansConfig((current) => {
      const plans = { ...current.catalog.plans };
      delete plans[planId];
      return { ...current, catalog: { ...current.catalog, plans } };
    });
  };

  const catalogForSave = () => ({
    ...plansConfig.catalog,
    plans: Object.fromEntries(Object.entries(plansConfig.catalog.plans || {}).map(([planId, plan]) => [
      planId,
      (plansConfig.fields || []).filter((field) => field.kind === 'tickers').reduce((next, field) => ({
        ...next,
        [field.name]: parseTickers(tickerText[`${planId}:${field.name}`] ?? (plan[field.name] || []).join(', '))
      }), { ...plan })
    ]))
  });

  const savePlans = async () => {
    setBusyKey('plans');
    setError('');
    setNotice('');
    try {
      const saved = await adminApi.savePlans(catalogForSave());
      const loaded = { ...plansConfig, catalog: saved.catalog };
      setPlansConfig(loaded);
      setTickerText(Object.fromEntries(Object.entries(saved.catalog?.plans || {}).map(([planId, plan]) => [`${planId}:included_tickers`, (plan.included_tickers || []).join(', ')])));
      setNotice('Plan catalog saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };

  const scheduleExpiry = (company) => {
    const key = `${company.tenant_id}:${company.ticker}`;
    const date = expiryDates[key] || company.expires_at?.slice(0, 10);
    if (!date) {
      setError('Choose an expiration date first.');
      return;
    }
    perform(
      `expiry-${key}`,
      () => adminApi.setCompanyExpiration(
        company.tenant_id,
        company.ticker,
        new Date(`${date}T23:59:59.000Z`).toISOString()
      ),
      `${company.ticker} will stop refreshing for ${company.user_email} after ${date}.`
    );
  };

  const visibleUsers = users.filter((account) =>
    `${account.email} ${account.plan} ${account.status}`.toLowerCase().includes(query.toLowerCase())
  );
  const planOptions = Object.entries(plansConfig.catalog?.plans || {});
  const planOptionsOrFallback = planOptions.length ? planOptions.map(([id, plan]) => [id, getPlanLabel(id, plan.label), plan]) : PLAN_OPTIONS.map(([id, label]) => [id, label, { label }]);
  const selectedInvitePlan = plansConfig.catalog?.plans?.[invitePlan];
  const companiesByTicker = companies.reduce((groups, company) => {
    const ticker = company.ticker.toUpperCase();
    groups[ticker] = [...(groups[ticker] || []), company];
    return groups;
  }, {});
  const rosterByTicker = new Map(trackedCompanies.map((company) => [company.ticker.toUpperCase(), company]));
  const companyRows = [...new Set([...rosterByTicker.keys(), ...Object.keys(companiesByTicker)])]
    .map((ticker) => {
      const rosterCompany = rosterByTicker.get(ticker);
      const subscriptions = companiesByTicker[ticker] || [];
      const research = rosterCompany?.research || subscriptions[0];
      return {
        ticker,
        rosterCompany,
        subscriptions,
        research,
        liveStatus: rosterCompany ? (rosterCompany.live ? 'Live' : 'Paused') : 'Existing access only',
        latestStatus: research?.latest_status || 'not_recorded',
        name: rosterCompany?.name || research?.company_name || ticker,
        subsection: rosterCompany?.subsection || rosterCompany?.industry_group || ''
      };
    });
  const companySubsections = [...new Set(companyRows.map((company) => company.subsection).filter(Boolean))].sort();
  const companyStatuses = [...new Set(companyRows.map((company) => company.latestStatus))].sort();
  const setCompanyFilter = (key, value) => setCompanyFilters((current) => ({ ...current, [key]: value }));
  const sortCompanies = (key) => setCompanySort((current) => ({
    key,
    direction: current.key === key && current.direction === 'ascending' ? 'descending' : 'ascending'
  }));
  const companySortValue = COMPANY_COLUMNS.find((column) => column.key === companySort.key).value;
  const visibleCompanyRows = companyRows
    .filter((company) =>
      `${company.ticker} ${company.name} ${company.subsection} ${company.subscriptions.map((subscription) => subscription.user_email).join(' ')}`
        .toLowerCase().includes(query.toLowerCase()) &&
      (!companyFilters.status || company.latestStatus === companyFilters.status) &&
      (!companyFilters.tracked || (companyFilters.tracked === 'tracked' ? company.subscriptions.length > 0 : company.subscriptions.length === 0)) &&
      (!companyFilters.subsection || (companyFilters.subsection === '__none__' ? !company.subsection : company.subsection === companyFilters.subsection)) &&
      (!companyFilters.live || company.liveStatus === companyFilters.live) &&
      (!companyFilters.pharma || (companyFilters.pharma === 'included' ? company.rosterCompany?.pharma_landscape === true : company.rosterCompany && !company.rosterCompany.pharma_landscape)) &&
      (!companyFilters.error || (companyFilters.error === 'failed' ? RESEARCH_FAILURE_STATUSES.has(company.latestStatus) : !RESEARCH_FAILURE_STATUSES.has(company.latestStatus)))
    )
    .sort((left, right) => {
      const leftValue = companySortValue(left);
      const rightValue = companySortValue(right);
      if (leftValue == null || rightValue == null) {
        if (leftValue !== rightValue) return leftValue == null ? 1 : -1;
      }
      const comparison = leftValue == null ? 0 : typeof leftValue === 'number'
        ? leftValue - rightValue
        : leftValue.localeCompare(rightValue);
      return (companySort.direction === 'ascending' ? comparison : -comparison) || left.ticker.localeCompare(right.ticker);
    });
  const trackedCompaniesFor = (account) => companies.filter(
    (company) => company.tenant_id === account.tenant_id
  );
  const matchesQuery = (ticker) => ticker.toLowerCase().includes(query.toLowerCase());
  const visibleCostCompanies = (costs.companies || []).filter((company) => matchesQuery(company.ticker));
  const visibleRuns = (costs.runs || []).filter((run) => matchesQuery(run.ticker));
  const visibleCoverageRequests = coverageRequests.filter((item) =>
    `${item.ticker} ${item.company_name || ''} ${(item.requesters || []).map((requester) => requester.email).join(' ')} ${item.execution_name || ''}`.toLowerCase().includes(query.toLowerCase())
  );
  const hubCatalog = visibilityConfig?.hubs || [];
  const hubAudienceColumns = ['default', 'public', ...Object.keys(plansConfig.catalog?.plans || {}).filter((planId) => planId !== 'public')];
  const cancelCoverage = async (item) => {
    if (!window.confirm(`Cancel coverage request for ${item.ticker}?`)) return;
    setBusyKey(`coverage-${item.request_id}`);
    setError('');
    setNotice('');
    try {
      await adminApi.cancelCoverage(item.request_id);
      await loadCoverage();
      setNotice(`${item.ticker} coverage request cancelled.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyKey('');
    }
  };
  const renderPlanField = (planId, field) => {
    const plan = plansConfig.catalog.plans[planId] || {};
    const value = plan[field.name];
    if (field.kind === 'bool') return <input aria-label={`${field.name} for ${planId}`} type="checkbox" checked={Boolean(value)} onChange={(event) => updatePlanField(planId, field.name, event.target.checked)} />;
    if (field.kind === 'billing_period') return <select aria-label={`${field.name} for ${planId}`} value={value || 'month'} onChange={(event) => updatePlanField(planId, field.name, event.target.value)}>{(plansConfig.billing_periods || []).map((period) => <option key={period} value={period}>{period.replaceAll('_', ' ')}</option>)}</select>;
    if (field.kind === 'ticker?') return <input aria-label={`${field.name} for ${planId}`} value={value || ''} maxLength="10" onChange={(event) => updatePlanField(planId, field.name, event.target.value.toUpperCase())} />;
    if (field.kind === 'tickers') {
      const key = `${planId}:${field.name}`;
      return <input aria-label={`${field.name} for ${planId}`} value={tickerText[key] ?? (value || []).join(', ')} onChange={(event) => setTickerText((current) => ({ ...current, [key]: event.target.value }))} onBlur={() => updatePlanField(planId, field.name, parseTickers(tickerText[key] ?? ''))} />;
    }
    if (field.kind === 'tabs?') {
      const options = plansConfig.deepdive_tabs || DEEPDIVE_TAB_OPTIONS;
      const chosen = value || [];
      const toggle = (id, on) => updatePlanField(planId, field.name, options.map((tab) => tab.id).filter((tab) => (tab === id ? on : chosen.includes(tab))));
      return <div className="count-field"><label><input aria-label={`All Deep Dive tabs for ${planId}`} type="checkbox" checked={value == null} onChange={(event) => updatePlanField(planId, field.name, event.target.checked ? null : [])} /> All tabs</label>{value != null && options.map((tab) => <label key={tab.id}><input aria-label={`${tab.label} for ${planId}`} type="checkbox" checked={chosen.includes(tab.id)} onChange={(event) => toggle(tab.id, event.target.checked)} /> {tab.label}</label>)}</div>;
    }
    if (field.kind === 'money?') return <div className="count-field"><label><input aria-label={`No negotiated price for ${planId}`} type="checkbox" checked={value == null} onChange={(event) => updatePlanField(planId, field.name, event.target.checked ? null : '0.00')} /> Not set</label><input aria-label={`${field.name} for ${planId}`} type="number" min="0" step="0.01" value={value ?? ''} disabled={value == null} onChange={(event) => updatePlanField(planId, field.name, event.target.value)} /></div>;
    if (field.kind === 'count?') return <div className="count-field"><label><input aria-label={`Unlimited ${field.name} for ${planId}`} type="checkbox" checked={value == null} onChange={(event) => updatePlanField(planId, field.name, event.target.checked ? null : 0)} /> Unlimited</label><input aria-label={`${field.name} for ${planId}`} type="number" min="0" step="1" value={value ?? ''} disabled={value == null} onChange={(event) => updatePlanField(planId, field.name, Number(event.target.value || 0))} /></div>;
    return <input aria-label={`${field.name} for ${planId}`} type={field.kind === 'int' ? 'number' : 'text'} min={field.kind === 'int' ? '0' : undefined} step={field.kind === 'int' ? '1' : undefined} value={value ?? ''} onChange={(event) => updatePlanField(planId, field.name, field.kind === 'int' ? Number(event.target.value || 0) : event.target.value)} />;
  };

  const userViewTabs = <div className="admin-subtabs" role="group" aria-label="User views">
    {[['accounts', 'Accounts'], ['activity', 'Report activity']].map(([value, label]) => <button key={value} type="button" className={userView === value ? 'active' : ''} aria-pressed={userView === value} onClick={() => setUserView(value)}>{label}</button>)}
  </div>;

  return (
    <div className="app-shell admin-shell">
      <header className="topbar">
        <Brand />
        <HeaderNav user={user} onWorkspace={onBack} onLogout={onLogout} onOpenProfile={onOpenProfile} />
      </header>

      <main className="admin-content">
        <section className="admin-heading">
          <div>
            <p className="eyebrow">Rebellionaire.AI / Operations</p>
            <h1>Admin console</h1>
          </div>
          <button className="secondary-button admin-refresh" onClick={() => tab === 'config' || tab === 'models' ? loadRuntimeConfig() : tab === 'visibility' ? loadVisibility() : tab === 'plans' ? loadPlans() : tab === 'coverage' ? loadCoverage() : perform('refresh', loadData, 'Data refreshed.')} disabled={Boolean(busyKey)}>
            Refresh data
          </button>
        </section>

        {!employeeAccess && <div className="admin-metrics" aria-label="Account and company totals">
          <div><span>Accounts</span><strong>{users.length}</strong></div>
          <div><span>Active subscriptions</span><strong>{companies.length}</strong></div>
          <div><span>Paused accounts</span><strong>{users.filter((account) => !account.enabled).length}</strong></div>
        </div>}

        <nav className="admin-tabs" aria-label="Admin sections">
          {!employeeAccess && <button className={tab === 'users' ? 'active' : ''} aria-pressed={tab === 'users'} onClick={() => { setTab('users'); setQuery(''); }}>Users</button>}
          <button className={tab === 'companies' ? 'active' : ''} aria-pressed={tab === 'companies'} onClick={() => { setTab('companies'); setQuery(''); }}>Companies</button>
          <button className={tab === 'client-reports' ? 'active' : ''} aria-pressed={tab === 'client-reports'} onClick={() => { setTab('client-reports'); setQuery(''); }}>Client Research</button>
          {!employeeAccess && <>
            <button className={tab === 'coverage' ? 'active' : ''} aria-pressed={tab === 'coverage'} onClick={() => { setTab('coverage'); setQuery(''); loadCoverage(); }}>Coverage</button>
            <button className={tab === 'costs' ? 'active' : ''} aria-pressed={tab === 'costs'} onClick={() => { setTab('costs'); setQuery(''); }}>Research cost</button>
            <button className={tab === 'plans' ? 'active' : ''} aria-pressed={tab === 'plans'} onClick={() => { setTab('plans'); setQuery(''); loadPlans(); }}>Plans</button>
            <button className={tab === 'config' ? 'active' : ''} aria-pressed={tab === 'config'} onClick={() => { setTab('config'); setQuery(''); if (!runtimeConfig) loadRuntimeConfig(); }}>Configuration</button>
            <button className={tab === 'models' ? 'active' : ''} aria-pressed={tab === 'models'} onClick={() => { setTab('models'); setQuery(''); if (!runtimeConfig) loadRuntimeConfig(); }}>Models</button>
            <button className={tab === 'visibility' ? 'active' : ''} aria-pressed={tab === 'visibility'} onClick={() => { setTab('visibility'); setQuery(''); loadVisibility(); }}>Visibility</button>
          </>}
          {tab !== 'config' && tab !== 'models' && tab !== 'visibility' && tab !== 'plans' && <label className="admin-search">
            <span className="sr-only">Search {tab}</span>
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={tab === 'users' ? (userView === 'activity' ? 'Find a reader or company' : 'Find an account') : 'Find a company or user'} />
          </label>}
        </nav>

        {error && <div className="error-box admin-message" role="alert">{error}</div>}
        {notice && <div className="success-box admin-message" role="status">{notice}</div>}

        {loading ? <div className="empty-state">Loading admin data...</div> : tab === 'users' && userView === 'activity' ? (
          <UserActivityPanel query={query} viewTabs={userViewTabs} />
        ) : tab === 'users' ? (
          <div className="admin-user-layout">
            <section className="panel invite-panel">
              <p className="eyebrow">New account</p>
              <h2>Send an invitation</h2>
              <p className="muted">Send a welcome email with a temporary password and a link to Rebellionaire. New clients choose a new password at first sign-in.</p>
              <form className="admin-invite-form" onSubmit={inviteUser}>
                <label>Email address<input type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} placeholder="name@company.com" required /></label>
                <label>Access tier
                  <select value={invitePlan} onChange={(event) => setInvitePlan(event.target.value)}>
                    {planOptionsOrFallback.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </label>
                <label className="admin-client-status"><input type="checkbox" checked={inviteIsClient || invitePlan === 'client'} disabled={invitePlan === 'client'} onChange={(event) => setInviteIsClient(event.target.checked)} />Client account - keep shared client companies when upgrading</label>
                <div className="plan-features" aria-live="polite">
                  {selectedInvitePlan ? planSummaryParts(selectedInvitePlan).map((feature) => <span key={feature}>{feature}</span>) : (PLAN_DETAILS[invitePlan] || []).map((feature) => <span key={feature}>{feature}</span>)}
                </div>
                <button className="primary-button" type="submit" disabled={Boolean(busyKey)}>{busyKey === 'invite' ? 'Sending...' : 'Send invitation'}</button>
              </form>
              <div className="bulk-invite">
                <p className="eyebrow">Bulk invitations</p>
                <p className="muted">Upload a CSV with an email column, or put email addresses in the first column. All clients will receive the selected access tier.</p>
                <label className="admin-upload-label">Client list (.csv)
                  <input aria-label="Upload client list" type="file" accept=".csv,text/csv" onChange={uploadInviteList} />
                </label>
                {bulkFileName && <div className="bulk-invite-preview" aria-live="polite">
                  <strong>{bulkFileName}</strong>
                  <span>{bulkEmails.length} unique {bulkEmails.length === 1 ? 'client' : 'clients'} ready</span>
                  {bulkDuplicateCount > 0 && <span>{bulkDuplicateCount} duplicate {bulkDuplicateCount === 1 ? 'address' : 'addresses'} skipped</span>}
                  {bulkInvalidRows.length > 0 && <span className="bulk-invite-invalid">Invalid or missing email on row{bulkInvalidRows.length === 1 ? '' : 's'} {bulkInvalidRows.join(', ')}. Correct the CSV before sending.</span>}
                </div>}
                <button className="primary-button" type="button" onClick={sendBulkInvites} disabled={!bulkEmails.length || Boolean(busyKey) || bulkInvalidRows.length > 0}>
                  {busyKey === 'bulk-invite' ? `Sending ${bulkProgress}...` : `Send ${bulkEmails.length || ''} invitation${bulkEmails.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </section>

            <section className="panel admin-table-panel">
              <div className="panel-heading"><div><p className="eyebrow">Access management</p><h2>Accounts</h2></div><div className="panel-heading-side">{userViewTabs}<span className="quality-label">{visibleUsers.length} shown</span></div></div>
              {visibleUsers.length ? <div className="admin-table-scroll">
                <table className="admin-table user-table">
                  <thead><tr><th>Account</th><th>Tier</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>{visibleUsers.map((account) => {
                    const trackedCompanies = trackedCompaniesFor(account);
                    const isExpanded = expandedAccount === account.username;
                    const needsInitialPassword = account.status === 'FORCE_CHANGE_PASSWORD';
                    return <React.Fragment key={account.username}>
                    <tr>
                    <td><button className="account-expand" aria-expanded={isExpanded} aria-controls={`account-companies-${account.username}`} onClick={() => setExpandedAccount(isExpanded ? '' : account.username)}><strong>{account.email || account.username}</strong><small>{account.status.replaceAll('_', ' ').toLowerCase()} · {trackedCompanies.length} tracked {trackedCompanies.length === 1 ? 'company' : 'companies'}</small></button></td>
                    <td><select aria-label={`Tier for ${account.email}`} value={account.plan} onChange={(event) => changePlan(account, event.target.value)} disabled={Boolean(busyKey)}>
                      {planOptionsOrFallback.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                    <label className="admin-client-status"><input type="checkbox" aria-label={`Client status for ${account.email}`} checked={account.is_client || account.plan === 'client'} disabled={Boolean(busyKey) || account.plan === 'client'} onChange={(event) => perform(
                      `client-${account.username}`,
                      () => adminApi.setClientStatus(account.username, event.target.checked),
                      `Client status updated for ${account.email}.`
                    )} />Client account</label></td>
                    <td><span className={`account-status ${account.enabled ? 'enabled' : 'paused'}`}>{account.enabled ? 'Active' : 'Paused'}</span></td>
                    <td><div className="admin-actions">
                      <button className="text-action" onClick={() => perform(`reset-${account.username}`, () => adminApi.sendPasswordReset(account.username), `${needsInitialPassword ? 'Invitation' : 'Password reset'} email sent to ${account.email}.`)} disabled={Boolean(busyKey)}>{needsInitialPassword ? 'Resend invite' : 'Reset password'}</button>
                      <button className="text-action" onClick={() => perform(`status-${account.username}`, () => account.enabled ? adminApi.pause(account.username) : adminApi.resume(account.username), `${account.email} ${account.enabled ? 'paused' : 'resumed'}.`)} disabled={Boolean(busyKey)}>{account.enabled ? 'Pause account' : 'Resume account'}</button>
                    </div></td>
                    </tr>
                    {isExpanded && <tr className="admin-detail-row"><td colSpan="4" id={`account-companies-${account.username}`}>
                      {trackedCompanies.length ? <div className="subscription-list">{trackedCompanies.map((company) => {
                        const key = `${company.tenant_id}:${company.ticker}`;
                        return <div className="subscription-item" key={key}>
                          <div className="subscription-company"><strong>{company.ticker}</strong><small>{company.company_name}</small></div>
                          <label>Access ends<input aria-label={`Expiration for ${company.ticker} tracked by ${company.user_email}`} type="date" value={expiryDates[key] ?? company.expires_at?.slice(0, 10) ?? ''} onChange={(event) => setExpiryDates((current) => ({ ...current, [key]: event.target.value }))} /></label>
                          <button className="text-action" onClick={() => scheduleExpiry(company)} disabled={Boolean(busyKey)}>Schedule</button>
                        </div>;
                      })}</div> : <div className="empty-state compact-empty">No active tracked companies for this account.</div>}
                    </td></tr>}
                    </React.Fragment>;
                  })}</tbody>
                </table>
              </div> : <div className="empty-state">No accounts match this search.</div>}
            </section>
          </div>
        ) : tab === 'client-reports' ? (
          <div className="admin-user-layout">
          <section className="panel invite-panel admin-config-panel" aria-label="Tamarac Focused Stocks">
            <div className="panel-heading">
              <div><p className="eyebrow">Synced from Tamarac</p><h2>{tamarac?.model || 'Focused Stocks'}</h2></div>
              <span className="quality-label">{tamarac?.sync?.status || 'Never synced'}</span>
            </div>
            <p className="muted">Each weekday morning the top-priority ticker of every sub-model in Tamarac's {tamarac?.model || 'Focused Stocks'} allocation model replaces the Tamarac entries in this list. Manually published reports are never touched; a removed Tamarac entry returns on the next sync.</p>
            {tamarac?.sync?.status ? <div className="subscription-list tamarac-sync">
              <div className="subscription-company"><strong>{tamarac.sync.status === 'running' ? 'Running since' : 'Finished'}</strong><small>{formatDate(tamarac.sync.status === 'running' ? tamarac.sync.started_at : tamarac.sync.finished_at)} · {tamarac.sync.requested_by || 'schedule'}</small></div>
              <div className="subscription-company"><strong>Tickers</strong><small>{(tamarac.sync.companies || []).map((company) => company.ticker).join(', ') || '—'}</small></div>
              {(tamarac.sync.added || []).length || (tamarac.sync.removed || []).length ? <div className="subscription-company"><strong>Changes</strong><small>Added {(tamarac.sync.added || []).join(', ') || 'none'} · removed {(tamarac.sync.removed || []).join(', ') || 'none'}</small></div> : null}
              {(tamarac.sync.unresolved || []).length ? <div className="subscription-company"><strong>No eligible symbol</strong><small>{tamarac.sync.unresolved.join(', ')}</small></div> : null}
              {tamarac.sync.error ? <div className="error-box" role="alert">Last sync failed: {tamarac.sync.error}</div> : null}
            </div> : null}
            <div className="admin-config-actions">
              <button className="primary-button" type="button" onClick={startTamaracSync} disabled={Boolean(busyKey) || tamarac?.sync?.status === 'running' || tamarac?.configured === false}>
                {busyKey === 'tamarac-sync' ? 'Starting...' : tamarac?.sync?.status === 'running' ? 'Sync running...' : 'Refresh from Tamarac'}
              </button>
            </div>
          </section>
          <section className="panel admin-table-panel">
            <div className="panel-heading"><div><p className="eyebrow">Client access</p><h2>Shared Client Research</h2></div><span className="quality-label">{clientReports.length} published</span></div>
            <form className="add-company-form" onSubmit={publishReport}>
              <label>Company name<input value={reportName} onChange={(event) => setReportName(event.target.value)} maxLength={120} required /></label>
              <label>Ticker<input value={reportTicker} onChange={(event) => setReportTicker(event.target.value)} maxLength={10} pattern="[A-Za-z0-9.-]+" required /></label>
              <button className="primary-button" type="submit" disabled={Boolean(busyKey)}>Publish report</button>
            </form>
            <div className="subscription-list">{clientReports.filter((report) => `${report.ticker} ${report.company_name}`.toLowerCase().includes(query.toLowerCase())).map((report) => <div className="subscription-item" key={report.ticker}>
              <div className="subscription-company"><strong>{report.ticker}</strong><small>{report.company_name}</small></div>
              <span className="pill" title={report.source === 'tamarac' ? report.sub_model || 'Tamarac sync' : 'Published by an admin'}>{report.source === 'tamarac' ? 'Tamarac' : 'Manual'}</span>
              <button className="text-action destructive-action" onClick={() => perform(`remove-report-${report.ticker}`, () => adminApi.removeClientReport(report.ticker), `${report.ticker} withdrawn.`)} disabled={Boolean(busyKey)}>Remove</button>
            </div>)}</div>
          </section>
          </div>
        ) : tab === 'coverage' ? (
          <section className="panel admin-table-panel">
            <div className="panel-heading">
              <div><p className="eyebrow">On-demand research</p><h2>Coverage requests</h2></div>
              <span className="quality-label">{visibleCoverageRequests.length} shown</span>
            </div>
            <div className="coverage-toolbar">
              <label>Status
                <select value={coverageStatus} onChange={(event) => { setCoverageStatus(event.target.value); loadCoverage(event.target.value); }}>
                  {COVERAGE_STATUSES.map((status) => <option key={status || 'all'} value={status}>{status || 'All statuses'}</option>)}
                </select>
              </label>
              <button className="secondary-button admin-refresh" type="button" onClick={() => loadCoverage()} disabled={coverageLoading || Boolean(busyKey)}>{coverageLoading ? 'Refreshing...' : 'Refresh'}</button>
            </div>
            {coverageLoading ? <div className="empty-state">Loading coverage requests...</div> : visibleCoverageRequests.length ? <div className="admin-table-scroll">
              <table className="admin-table coverage-table">
                <thead><tr><th>Ticker</th><th>Status</th><th>Opener</th><th>Created</th><th>SLA due</th><th>Execution</th><th>Error</th><th>Actions</th></tr></thead>
                <tbody>{visibleCoverageRequests.map((item) => {
                  const requesters = item.requesters || [];
                  const canCancel = ['pending', 'building'].includes(item.status);
                  return <tr key={item.request_id}>
                    <td><strong>{item.ticker}</strong><small>{item.company_name || item.request_id}</small></td>
                    <td><span className={`pill ${item.status === 'delivered' ? 'succeeded' : canCancel ? 'running' : ['failed', 'refused', 'cancelled'].includes(item.status) ? 'failed' : ''}`}>{item.status}</span></td>
                    <td>{requesters[0]?.email || '—'}<small>{requesters.length} {requesters.length === 1 ? 'requester' : 'requesters'}</small></td>
                    <td>{formatDate(item.created_at)}</td>
                    <td>{formatDate(item.sla_due)}</td>
                    <td>{item.execution_name || '—'}</td>
                    <td>{item.error || '—'}</td>
                    <td>{canCancel ? <button className="text-action destructive-action" type="button" onClick={() => cancelCoverage(item)} disabled={Boolean(busyKey)}>Cancel</button> : '—'}</td>
                  </tr>;
                })}</tbody>
              </table>
            </div> : <div className="empty-state">No coverage requests match this filter.</div>}
          </section>
        ) : tab === 'costs' ? (
          <div className="admin-columns">
            <section className="panel admin-table-panel">
              <div className="panel-heading">
                <div><p className="eyebrow">Model spend</p><h2>Cost per company</h2></div>
              </div>
              <div className="admin-metrics" aria-label="Research cost averages across all companies">
                <div><span>Avg refresh cost</span><strong>{formatUsd(costs.average_refresh_run_cost_usd)}</strong></div>
                <div><span>Avg full report cost</span><strong>{formatUsd(costs.average_full_run_cost_usd)}</strong></div>
                <div><span>Total cost:</span><strong>{formatUsd(costs.total_cost_usd)}</strong></div>
              </div>
              {visibleCostCompanies.length ? <div className="admin-table-scroll">
                <table className="admin-table cost-table company-cost-table">
                  <thead><tr><th>Company</th><th>Type</th><th className="r">Runs</th><th className="r">Avg cost</th><th className="r">Avg time</th><th className="r">Last run</th><th className="r">Total cost</th></tr></thead>
                  {visibleCostCompanies.map((company) => (
                    <tbody key={company.ticker} className="company-cost-group">
                      {['refresh', 'full'].map((kind) => (
                        <tr key={`${company.ticker}-${kind}`}>
                          {kind === 'refresh' && <td rowSpan={2} className="company-cost-name"><strong>{company.ticker}</strong></td>}
                          <td>{kind === 'refresh' ? 'Refresh' : 'Full report'}</td>
                          <td className="r">{kind === 'full' ? company.full_runs : company.runs - company.full_runs}</td>
                          <td className="r">{formatUsd(company[`average_${kind}_run_cost_usd`])}</td>
                          <td className="r">{formatMinutes(company[`average_${kind}_run_seconds`])}</td>
                          <td className="r" title={company[`last_${kind}_run_at`] ? formatDate(company[`last_${kind}_run_at`]) : undefined}>{formatUsd(company[`last_${kind}_run_cost_usd`])}</td>
                          <td className="r">{formatUsd(company[`${kind}_run_cost_usd`])}</td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                </table>
              </div> : <div className="empty-state">No research spend has been recorded yet.</div>}
            </section>

            <section className="panel admin-table-panel">
              <div className="panel-heading"><div><p className="eyebrow">Where it goes</p><h2>Cost by stage</h2></div></div>
              <p className="muted">Average per run across all companies, including zero for skipped stages.</p>
              {['full', 'refresh'].map((kind) => {
                const group = costs.stage_averages?.[kind];
                const label = kind === 'full' ? 'Full report' : 'Refresh';
                return (
                  <section key={kind} aria-label={`${label} stage averages`}>
                    <div className="panel-heading"><h3>{label}</h3><span className="quality-label">{group?.runs ?? 0} runs</span></div>
                    {group?.stages.length ? <div className="admin-table-scroll">
                      <table className="admin-table cost-table">
                        <thead><tr><th>Stage</th><th className="r">Avg calls</th><th className="r">Avg in tokens</th><th className="r">Avg out tokens</th><th className="r">Avg model time</th><th className="r">Avg cost</th></tr></thead>
                        <tbody>{group.stages.map((row) => (
                          <tr key={row.stage}>
                            <td>{row.stage}</td>
                            <td className="r">{formatTokens(row.calls)}</td>
                            <td className="r">{formatTokens(row.prompt_tokens)}</td>
                            <td className="r">{formatTokens(row.completion_tokens)}</td>
                            <td className="r">{formatMinutes(row.model_seconds)}</td>
                            <td className="r">{formatUsd(row.cost_usd)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div> : <div className="empty-state">No {label.toLowerCase()} stage usage has been recorded yet.</div>}
                  </section>
                );
              })}
            </section>

            <section className="panel admin-table-panel">
              <div className="panel-heading"><div><p className="eyebrow">Over time</p><h2>Recent runs</h2></div><span className="quality-label">{visibleRuns.length} shown</span></div>
              <div className="admin-table-scroll">
                <table className="admin-table cost-table">
                  <thead><tr><th>Started</th><th>Company</th><th>Kind</th><th>Stage timings</th><th className="r">Calls</th><th className="r">Time</th><th className="r">Cost</th></tr></thead>
                  <tbody>{visibleRuns.slice(0, 100).map((run) => (
                    <tr key={`${run.ticker}-${run.run_id}`}>
                      <td>{formatDate(run.started_at)}</td>
                      <td><strong>{run.ticker}</strong></td>
                      <td>{run.full_research ? 'Full research' : 'Refresh'}<small>{run.reason || 'unknown trigger'}</small></td>
                      <td><div className="stage-timings">{(run.stages || []).map((stage) => (
                        <span key={stage.stage} className={stage.stage === run.slowest_stage ? 'slowest' : undefined}>
                          {stage.stage} {formatMinutes(stage.seconds)}
                        </span>
                      ))}</div></td>
                      <td className="r">{run.calls}</td>
                      <td className="r">{formatMinutes(run.seconds)}</td>
                      <td className="r">{formatUsd(run.cost_usd)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
          </div>
        ) : tab === 'plans' ? (
          plansLoading ? <div className="empty-state">Loading plan catalog...</div> : (
            <div className="admin-columns">
              <section className="panel admin-table-panel plans-panel">
                <div className="panel-heading"><div><p className="eyebrow">Entitlements</p><h2>Plans</h2></div><span className="quality-label">Version {plansConfig.catalog.version}</span></div>
                <p className="muted">First Look access is monthly: Free Tier gets the admin-assigned company, Client gets shared reports published under Client First Looks plus five personal picks, Tier 1–3 get 7/14/35 personal picks, and Employees get all First Looks. Configure allowances and the Free Tier ticker here. Other products keep their existing rules. For negotiated terms, add a unique plan ID (for example, customer_acme), configure features and price, then assign it to that customer's account. Customer-specific plans and prices are hidden from public listings; prices are informational because billing is not connected.</p>
                <div className="admin-table-scroll">
                  <table className="admin-table plans-table">
                    <thead><tr><th>Plan</th>{(plansConfig.fields || []).filter((field) => field.name !== 'credits_per_period').map((field) => <th key={field.name}>{PLAN_FIELD_LABELS[field.name] || field.name.replaceAll('_', ' ')}<small>{field.kind}</small></th>)}<th>Actions</th></tr></thead>
                    <tbody>{Object.entries(plansConfig.catalog.plans || {}).map(([planId, plan]) => (
                      <tr key={planId}><td><strong>{getPlanLabel(planId, plan.label)}</strong><small>{planId}</small><small>{planSummary(plan)}</small></td>{(plansConfig.fields || []).filter((field) => field.name !== 'credits_per_period').map((field) => <td key={field.name}>{renderPlanField(planId, field)}</td>)}<td><button className="text-action destructive-action" type="button" onClick={() => removePlan(planId)} disabled={Object.keys(plansConfig.catalog.plans || {}).length <= 1}>Remove</button></td></tr>
                    ))}</tbody>
                  </table>
                </div>
                <div className="admin-config-actions">
                  <label className="add-plan-id">New plan id<input aria-label="New plan id" value={newPlanId} onChange={(event) => setNewPlanId(event.target.value)} placeholder="customer_acme" /></label>
                  <button className="secondary-button" type="button" onClick={addPlan}>Add plan</button>
                  <button className="primary-button" type="button" onClick={savePlans} disabled={Boolean(busyKey)}>{busyKey === 'plans' ? 'Saving...' : 'Save plans'}</button>
                </div>
              </section>
            </div>
          )
        ) : tab === 'config' || tab === 'models' ? (
          runtimeConfigLoading ? <div className="empty-state">Loading research defaults...</div> : runtimeConfig ? (
            <div className="admin-columns">
              {tab === 'config' && <section className="panel admin-config-panel">
                <div className="panel-heading">
                  <div><p className="eyebrow">Run behavior</p><h2>Research tuning</h2></div>
                  <span className="quality-label">Applies to new work</span>
                </div>
                <p className="muted">These defaults control research model roles, search behavior, and fact limits. A zero fact limit means no cap.</p>
                <div className="admin-config-grid">
                  <label>Search model role<select value={runtimeConfig.tuning.search_role} onChange={(event) => updateTuning('search_role', event.target.value)}>{Object.keys(runtimeConfig.models).map((role) => <option key={role} value={role}>{role}</option>)}</select></label>
                  <label>Extraction model role<select value={runtimeConfig.tuning.extract_role} onChange={(event) => updateTuning('extract_role', event.target.value)}>{Object.keys(runtimeConfig.models).map((role) => <option key={role} value={role}>{role}</option>)}</select></label>
                  <label>Discovery model role<select value={runtimeConfig.tuning.discovery_role} onChange={(event) => updateTuning('discovery_role', event.target.value)}>{Object.keys(runtimeConfig.models).map((role) => <option key={role} value={role}>{role}</option>)}</select></label>
                  <label>Analysis model role<select value={runtimeConfig.tuning.analysis_role} onChange={(event) => updateTuning('analysis_role', event.target.value)}>{Object.keys(runtimeConfig.models).map((role) => <option key={role} value={role}>{role}</option>)}</select></label>
                  <label>Search context size<select value={runtimeConfig.tuning.search_context_size} onChange={(event) => updateTuning('search_context_size', event.target.value)}>{CONTEXT_SIZE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                  <label>Search reasoning effort<select value={runtimeConfig.tuning.search_reasoning_effort} onChange={(event) => updateTuning('search_reasoning_effort', event.target.value)}>{REASONING_EFFORT_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                  <label>Maximum research targets<input type="number" min="0" step="1" value={runtimeConfig.tuning.max_targets} onChange={(event) => updateTuning('max_targets', Number(event.target.value))} /></label>
                  <label>Analysis fact limit<input type="number" min="0" step="1" value={runtimeConfig.tuning.analysis_fact_limit} onChange={(event) => updateTuning('analysis_fact_limit', Number(event.target.value))} /></label>
                  <label>Report fact limit<input type="number" min="0" step="1" value={runtimeConfig.tuning.report_fact_limit} onChange={(event) => updateTuning('report_fact_limit', Number(event.target.value))} /></label>
                </div>
              </section>}

              {tab === 'models' && <section className="panel admin-config-panel">
                <div className="panel-heading"><div><p className="eyebrow">Model registry</p><h2>Model roles and pricing</h2></div></div>
                <p className="muted">Model selection presets provider and pricing and lowers the output cap when required. Only compatible providers are offered for each role. The cheaper role controls budget research; report_haiku controls Haiku report generation.</p>
                <p className="muted">Opus 5.5 and Sonnet 5.5 are available without changing existing assignments. Use current pricing updates only a role's cost metadata; click Save models to apply it. Published input / output rates per million tokens: Opus 4.8 $5 / $25, Opus 5.5 $4 / $20, and Sonnet 5 or 5.5 $2 / $10. Historical usage records are unchanged.</p>
                <p className="muted">Haiku 5.5 uses conservative pricing of $0.50 input / $2.50 output per million tokens for prompts over 100K tokens. Prompts up to 100K cost $0.10 / $0.50. These flat-rate estimates can be edited for your workload.</p>
                <p className="muted">The newsdata role is the NewsData.io news API used by the daily news lane, not a model. Each request that returns articles uses one credit; set USD per call to the plan price divided by its monthly credits (Basic: $199.99 / 20,000 = $0.01).</p>
                <div className="admin-config-models">
                  {Object.entries(runtimeConfig.models).map(([role, model]) => (
                    <div className="admin-config-model" key={role}>
                      <h3>{role}{role === runtimeConfig.tuning.search_role ? ' · active search role' : ''}</h3>
                      <div className="admin-config-grid">
                        <label>Model<select value={model.model_id} onChange={(event) => updateModel(role, 'model_id', event.target.value)}>{runtimeConfig.model_options.filter((option) => option.model_id === model.model_id || (runtimeConfig.model_role_providers?.[role] || [model.provider]).includes(option.provider)).map((option) => <option key={option.model_id} value={option.model_id}>{modelOptionLabel(option)}</option>)}</select></label>
                        <label>Provider<input value={model.provider} readOnly aria-label={`${role} provider`} /></label>
                        <label>Input USD / million tokens<input type="number" min="0" step="any" value={model.cost_in ?? ''} onChange={(event) => updateModel(role, 'cost_in', event.target.value === '' ? null : Number(event.target.value))} /></label>
                        <label>Output USD / million tokens<input type="number" min="0" step="any" value={model.cost_out ?? ''} onChange={(event) => updateModel(role, 'cost_out', event.target.value === '' ? null : Number(event.target.value))} /></label>
                        <label>Maximum output tokens<input type="number" min="1" max={runtimeConfig.model_options.find((option) => option.model_id === model.model_id && option.provider === model.provider)?.max_tokens} step="1" value={model.max_tokens} onChange={(event) => updateModel(role, 'max_tokens', Number(event.target.value))} /></label>
                        <label>USD per call<input type="number" min="0" step="any" value={model.cost_per_call} onChange={(event) => updateModel(role, 'cost_per_call', Number(event.target.value))} /></label>
                      </div>
                      <button className="text-action" type="button" aria-label={`Use current pricing for ${role}`} onClick={() => applyCurrentModelPricing(role)} disabled={Boolean(busyKey) || !runtimeConfig.model_options.some((option) => option.model_id === model.model_id && option.provider === model.provider)}>Use current pricing</button>
                    </div>
                  ))}
                </div>
              </section>}
              <section className="panel admin-config-panel">
                <div className="admin-config-actions">
                  <span className="muted">Saved defaults are shared across research services; in-flight work may finish with settings already loaded.</span>
                  <button className="secondary-button" onClick={loadRuntimeConfig} disabled={Boolean(busyKey)}>Reload saved defaults</button>
                  <button className="primary-button" onClick={saveRuntimeConfig} disabled={Boolean(busyKey)}>{busyKey === 'research-config' ? 'Saving...' : tab === 'models' ? 'Save models' : 'Save defaults'}</button>
                </div>
              </section>
            </div>
          ) : <div className="empty-state">Research defaults could not be loaded.</div>
        ) : tab === 'visibility' ? (
          visibilityLoading ? <div className="empty-state">Loading visibility matrix...</div> : visibilityConfig ? (
            <div className="admin-columns">
              <section className="panel admin-table-panel visibility-panel">
                <div className="panel-heading">
                  <div><p className="eyebrow">Report access</p><h2>Visibility matrix</h2></div>
                  <span className="quality-label">Version {visibilityConfig.matrix.version}</span>
                </div>
                <p className="muted">Defaults define each audience. Item-level values that differ from the default are saved as overrides.</p>
                <div className="admin-table-scroll">
                  <table className="admin-table visibility-table">
                    <thead><tr><th>Item</th>{visibilityConfig.audiences.map((audience) => <th key={audience}>{accessAudienceLabel(audience, visibilityConfig.audience_labels)}</th>)}</tr></thead>
                    <tbody>
                      <tr className="visibility-defaults-row"><td><strong>Defaults</strong><small>Audience fallback</small></td>{visibilityConfig.audiences.map((audience) => (
                        <td key={audience}><select aria-label={`Default visibility for ${audience}`} value={visibilityConfig.matrix.defaults[audience]} onChange={(event) => setVisibilityDefault(audience, event.target.value)}>
                          {visibilityConfig.levels.map((level) => <option key={level} value={level}>{level}</option>)}
                        </select></td>
                      ))}</tr>
                      {(visibilityConfig.products || [{ id: 'deepdive', label: 'Deep Dive' }]).flatMap((product) => [
                        <tr className="visibility-product-row" key={`${product.id}-section`}><td colSpan={visibilityConfig.audiences.length + 1}><strong>{product.label}</strong></td></tr>,
                        ...visibilityConfig.items.filter((item) => item.id.startsWith(`${product.id}.`)).map((item) => (
                        <tr key={item.id}><td><strong>{item.label}</strong><small>{item.id}</small></td>{visibilityConfig.audiences.map((audience) => {
                          const override = visibilityConfig.matrix.items?.[item.id]?.[audience];
                          const value = override || visibilityConfig.matrix.defaults[audience];
                          return <td key={audience} className={override ? 'visibility-override' : undefined}><select aria-label={`${item.label} visibility for ${audience}`} value={value} onChange={(event) => setVisibilityItem(item.id, audience, event.target.value)}>
                            {visibilityConfig.levels.map((level) => <option key={level} value={level}>{level}</option>)}
                          </select>{override && <small>Override</small>}</td>;
                        })}</tr>
                      ))])}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="panel admin-config-panel visibility-settings">
                <div className="panel-heading"><div><p className="eyebrow">Release rules</p><h2>Timing and showcase</h2></div></div>
                <div className="admin-table-scroll">
                  <table className="admin-table visibility-table compact">
                    <thead><tr><th>Rule</th>{visibilityConfig.audiences.map((audience) => <th key={audience}>{accessAudienceLabel(audience, visibilityConfig.audience_labels)}</th>)}</tr></thead>
                    <tbody>
                      <tr><td><strong>Release delay</strong><small>Hours</small></td>{visibilityConfig.audiences.map((audience) => (
                        <td key={audience}><input aria-label={`Release delay hours for ${audience}`} type="number" min="0" step="1" value={visibilityConfig.matrix.release_delay_hours[audience]} onChange={(event) => setVisibilityAudienceValue('release_delay_hours', audience, Number(event.target.value || 0))} /></td>
                      ))}</tr>
                      <tr><td><strong>Archive depth</strong><small>Days, blank = full history</small></td>{visibilityConfig.audiences.map((audience) => (
                        <td key={audience}><input aria-label={`Archive depth days for ${audience}`} type="number" min="0" step="1" value={visibilityConfig.matrix.archive_depth_days[audience] ?? ''} onChange={(event) => setVisibilityAudienceValue('archive_depth_days', audience, event.target.value === '' ? null : Number(event.target.value))} /></td>
                      ))}</tr>
                    </tbody>
                  </table>
                </div>
                <label className="showcase-input">Showcase tickers<input aria-label="Showcase tickers" value={showcaseText} onChange={(event) => setShowcaseText(event.target.value)} onBlur={() => updateVisibilityMatrix((matrix) => ({ ...matrix, showcase_tickers: parseShowcase(showcaseText) }))} /></label>
                <div className="admin-config-actions">
                  <span className="muted">Save sends the loaded version so stale edits are rejected by the API.</span>
                  <button className="primary-button" onClick={saveVisibility} disabled={Boolean(busyKey)}>{busyKey === 'visibility' ? 'Saving...' : 'Save visibility'}</button>
                </div>
              </section>

              {hubCatalog.length > 0 && <section className="panel admin-table-panel visibility-panel hubs-visibility-panel">
                <div className="panel-heading">
                  <div><p className="eyebrow">Cross-company hubs</p><h2>Hubs</h2></div>
                  <span className="quality-label">{hubCatalog.length} hubs</span>
                </div>
                <p className="muted">Hub scopes are configured by default, public, and each plan. Values are limited to each hub's allowed scopes.</p>
                <div className="admin-table-scroll">
                  <table className="admin-table visibility-table">
                    <thead><tr><th>Hub</th>{hubAudienceColumns.map((audience) => <th key={audience}>{accessAudienceLabel(audience, visibilityConfig.audience_labels)}</th>)}</tr></thead>
                    <tbody>{hubCatalog.map((hub) => {
                      const allowedScopes = hub.scopes || ['none', 'all'];
                      const hubValues = visibilityConfig.matrix.hubs?.[hub.id] || {};
                      return (
                        <tr key={hub.id}><td><strong>{hub.label || hub.id}</strong><small>{hub.id}</small></td>{hubAudienceColumns.map((audience) => {
                          const value = hubValues[audience] ?? (audience === 'default' ? allowedScopes[0] : hubValues.default ?? allowedScopes[0]);
                          return <td key={audience}><select aria-label={`${hub.label || hub.id} scope for ${audience}`} value={value} onChange={(event) => setHubVisibility(hub.id, audience, event.target.value)}>{allowedScopes.map((scope) => <option key={scope} value={scope}>{scope}</option>)}</select></td>;
                        })}</tr>
                      );
                    })}</tbody>
                  </table>
                </div>
                <div className="hub-rebuild">
                  <div><p className="eyebrow">Build</p><h3>Rebuild hubs</h3><p className="muted">Starts the roster hubs build workflow for selected hubs.</p></div>
                  <div className="hub-rebuild-list">{hubCatalog.map((hub) => <label key={hub.id}><input type="checkbox" checked={Boolean(hubBuildSelection[hub.id])} onChange={(event) => toggleHubBuild(hub.id, event.target.checked)} /> {hub.label || hub.id}</label>)}</div>
                  <label className="hub-rebuild-taxonomy"><input type="checkbox" checked={hubBuildTaxonomy} onChange={(event) => setHubBuildTaxonomy(event.target.checked)} /> Rebuild taxonomy first</label>
                  <button className="secondary-button" type="button" onClick={rebuildHubs} disabled={Boolean(busyKey)}>{busyKey === 'hubs-build' ? 'Starting...' : 'Rebuild hubs'}</button>
                </div>
              </section>}
            </div>
          ) : <div className="empty-state">Visibility settings could not be loaded.</div>
        ) : (
          <section className="panel admin-table-panel company-admin-panel">
            <div className="panel-heading"><div><p className="eyebrow">Shared research universe and account access</p><h2>Tracked companies</h2></div><span className="quality-label">{visibleCompanyRows.length} shown</span></div>
            <p className="muted">Live companies can be selected against a user's monthly allowance; employees can select from the full live list. Pause prevents new picks but preserves existing access and reports. Priming does not start research.</p>
            <p className="muted">Profile is the configured research depth, not build completion. First Look tracking requires at least lite; Deep Dive access requires full. Tracking upgrades profiles and starts the required build automatically. Admins cannot select a profile below the highest active tracking requirement. Removing picks does not automatically downgrade profiles. Status is roster availability, not research health. Last updated is the most recent successful company research step; Latest update shows the latest started step and its recorded outcome, not the outcome of the whole build. Last failure is retained after later successes. Shared daily scans do not imply that every company's reports were rebuilt.</p>
            <p className="muted">To add back a removed company, enter its ticker below and start a research build. Successful admission restores roster membership and preserves existing research and profile depth.</p>
            <p className="muted">Pharma Landscape is an independent roster flag, not a subsection or pick status. Changes apply on the next daily refresh. Enabling a company may trigger paid source research and asset extraction; no_profile companies cannot be enabled. With none selected, the next refresh publishes an empty landscape.</p>
            {canDeleteCompany && <p className="muted">QA roster cleanup: Delete removes roster membership, not research data or existing user access. Companies with existing allocations may remain listed as “Existing access only”.</p>}
            <form className="add-company-form" onSubmit={addTrackedCompany}>
              <label><span>Build one company</span><input aria-label="Ticker to build" value={trackedTicker} onChange={(event) => setTrackedTicker(event.target.value)} placeholder="e.g. UPST" maxLength={10} pattern="[A-Za-z0-9.-]+" required /></label>
              <button className="primary-button" type="submit" disabled={Boolean(busyKey)}>{busyKey.startsWith('build-') ? 'Starting build...' : 'Start research build'} <span aria-hidden="true">+</span></button>
            </form>
            <div className="company-filters">
              <label>Research status<select value={companyFilters.status} onChange={(event) => setCompanyFilter('status', event.target.value)}>
                <option value="">All research statuses</option>
                {companyStatuses.map((status) => <option key={status} value={status}>{status.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase())}</option>)}
              </select></label>
              <label>Tracked by<select value={companyFilters.tracked} onChange={(event) => setCompanyFilter('tracked', event.target.value)}>
                <option value="">Any client count</option><option value="tracked">Tracked by anyone</option><option value="untracked">Not tracked by anyone</option>
              </select></label>
              <label>Subsection<select value={companyFilters.subsection} onChange={(event) => setCompanyFilter('subsection', event.target.value)}>
                <option value="">All subsections</option><option value="__none__">No subsection</option>
                {companySubsections.map((subsection) => <option key={subsection} value={subsection}>{subsection}</option>)}
              </select></label>
              <label>Live / paused<select value={companyFilters.live} onChange={(event) => setCompanyFilter('live', event.target.value)}>
                <option value="">All roster states</option><option value="Live">Live only</option><option value="Paused">Paused only</option><option value="Existing access only">Existing access only (off roster)</option>
              </select></label>
              <label>Latest update error<select value={companyFilters.error} onChange={(event) => setCompanyFilter('error', event.target.value)}>
                <option value="">Any latest update</option><option value="failed">Latest update failed</option><option value="no_error">No latest update error</option>
              </select></label>
              <label>Pharma Landscape<select value={companyFilters.pharma} onChange={(event) => setCompanyFilter('pharma', event.target.value)}>
                <option value="">All landscape memberships</option><option value="included">Included</option><option value="excluded">Not included</option>
              </select></label>
              <button className="text-action" type="button" onClick={() => { setCompanyFilters(EMPTY_COMPANY_FILTERS); setQuery(''); }}>Clear filters</button>
            </div>
            {visibleCompanyRows.length ? <div className="admin-table-scroll">
              <table className="admin-table company-table combined-company-table">
                <thead><tr>{COMPANY_COLUMNS.map(({ key, label }) => <th key={key} scope="col" aria-sort={companySort.key === key ? companySort.direction : 'none'}>
                  <button className="company-sort" type="button" aria-label={`Sort by ${label}`} onClick={() => sortCompanies(key)}>{label}<span aria-hidden="true">{companySort.key === key ? (companySort.direction === 'ascending' ? ' ^' : ' v') : ' -'}</span></button>
                </th>)}</tr></thead>
                <tbody>{visibleCompanyRows.map(({ ticker, name, subsection, rosterCompany, subscriptions, research }) => {
                  const isExpanded = expandedCompany === ticker;
                  const latestStatus = research?.latest_status;
                  return <React.Fragment key={ticker}>
                    <tr>
                      <td><strong>{ticker}</strong><small>{name}</small></td>
                      <td>{subsection || '—'}</td>
                      <td>{rosterCompany && !employeeAccess ? <>
                        <select aria-label={`Profile for ${ticker}`} value={rosterCompany.profile_level} onChange={(event) => setCompanyProfile(rosterCompany, event.target.value)} disabled={Boolean(busyKey)}>
                          {PROFILE_LEVELS.map((level) => <option key={level} value={level} disabled={PROFILE_LEVELS.indexOf(level) < PROFILE_LEVELS.indexOf(rosterCompany.minimum_profile || 'no_profile') || (level === 'no_profile' && rosterCompany.pharma_landscape)}>{level}</option>)}
                        </select>
                        {rosterCompany.minimum_profile && rosterCompany.minimum_profile !== 'no_profile' && <small>Client minimum: {rosterCompany.minimum_profile}</small>}
                      </> : rosterCompany?.profile_level || '—'}</td>
                      <td>{rosterCompany ? <span className={`pill ${rosterCompany.live ? 'succeeded' : ''}`}>{rosterCompany.live ? 'Live' : 'Paused'}</span> : <span className="pill">Existing access only</span>}</td>
                      <td>{rosterCompany ? <input type="checkbox" aria-label={`Include ${ticker} in Pharma Landscape`} checked={rosterCompany.pharma_landscape === true} onChange={(event) => setCompanyPharmaLandscape(rosterCompany, event.target.checked)} disabled={Boolean(busyKey) || (rosterCompany.profile_level === 'no_profile' && !rosterCompany.pharma_landscape)} /> : <input type="checkbox" aria-label={`Include ${ticker} in Pharma Landscape`} checked={false} disabled />}</td>
                      <td>{subscriptions.length ? <button className="text-action tracked-count" aria-expanded={isExpanded} aria-controls={`company-clients-${ticker}`} onClick={() => setExpandedCompany(isExpanded ? '' : ticker)}>{subscriptions.length} {subscriptions.length === 1 ? 'client' : 'clients'}</button> : '0 clients'}</td>
                      <td>{research?.last_updated ? formatDate(research.last_updated) : 'Not recorded'}</td>
                      <td>{latestStatus ? <span className={`pill ${latestStatus}`}>{latestStatus.replaceAll('_', ' ')}</span> : 'Not recorded'}{research?.latest_step && <small title={research.latest_at ? formatDate(research.latest_at) : undefined}>{research.latest_step}</small>}</td>
                      <td>{research?.last_failure_at ? <span title={research.last_failure || ''}>{formatDate(research.last_failure_at)}</span> : 'None recorded'}</td>
                      <td><div className="company-actions">
                        {rosterCompany && <button className="text-action" type="button" onClick={() => setCompanyLive(rosterCompany, !rosterCompany.live)} disabled={Boolean(busyKey)}>{rosterCompany.live ? `Pause ${ticker}` : `Resume ${ticker}`}</button>}
                        {canDeleteCompany && rosterCompany && <button className="text-action destructive-action" type="button" onClick={() => deleteCompany(rosterCompany)} disabled={Boolean(busyKey)}>Delete {ticker} from roster</button>}
                        <button className="text-action" type="button" onClick={() => perform(`build-${ticker}`, () => rosterApi.buildCompany(ticker, { admit_options: { lite: rosterCompany?.profile_level === 'lite' } }), `${ticker} research build started.`)} disabled={Boolean(busyKey)}>Build {ticker}</button>
                      </div></td>
                    </tr>
                    {isExpanded && subscriptions.length > 0 && <tr className="admin-detail-row"><td colSpan={COMPANY_COLUMNS.length} id={`company-clients-${ticker}`}>
                      <div className="subscription-list">{subscriptions.map((subscription) => {
                        const key = `${subscription.tenant_id}:${subscription.ticker}`;
                        return <div className="subscription-item" key={key}>
                          <div className="subscription-company"><strong>{subscription.user_email}</strong><small>Client</small></div>
                          <label>Access ends<input aria-label={`Expiration for ${ticker} tracked by ${subscription.user_email}`} type="date" value={expiryDates[key] ?? subscription.expires_at?.slice(0, 10) ?? ''} onChange={(event) => setExpiryDates((current) => ({ ...current, [key]: event.target.value }))} /></label>
                          <div className="admin-actions">
                            <button className="text-action" onClick={() => scheduleExpiry(subscription)} disabled={Boolean(busyKey)}>Schedule</button>
                            <button className="text-action destructive-action" onClick={() => perform(`remove-${key}`, () => adminApi.removeCompany(subscription.tenant_id, subscription.ticker), `${ticker} removed for ${subscription.user_email}.`)} disabled={Boolean(busyKey)}>Remove</button>
                          </div>
                        </div>;
                      })}</div>
                    </td></tr>}
                  </React.Fragment>;
                })}</tbody>
              </table>
            </div> : <div className="empty-state">No companies match this search.</div>}
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminPage;